import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores";
import { usePermissions } from "@/composables/usePermissions";
import { PERMISSIONS } from "@/constants";
import { fetchTranslations } from "@/composables/useTranslation";

import NotFoundView from "@/views/NotFoundView.vue";

const routes = [
  {
    path: "/",
    name: "/",
    redirect: () => {
      const { getDefaultRoute } = usePermissions();
      const auth = useAuthStore();

      if (!auth.user || !auth.user.roles) {
        return { name: "login_passwordless", params: { locale: "en" } };
      }

      const routeName = getDefaultRoute();
      return { name: routeName };
    },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: function () {
      return import("@/views/DashboardView.vue");
    },
    meta: {
      title: "Dashboard",
      requiresAuth: true,
      permission: "VIEW_DASHBOARD",
    },
  },

  {
    path: "/home",
    name: "home",
    component: function () {
      return import("@/views/participant/Home.vue");
    },
    meta: {
      title: "Home",
      requiresAuth: true,
      permission: "VIEW_HOME",
    },
  },

  {
    path: "/monitoring-dashboard/:taskId",
    name: "monitoringDashboard",
    component: function () {
      return import("@/views/projects/MonitoringDashboardView.vue");
    },
    meta: {
      title: "Monitoring Dashboard",
      requiresAuth: true,
    },
  },

  {
    path: "/:locale/login",
    name: "login",
    component: function () {
      return import("@/views/security/LoginView.vue");
    },
    meta: { title: "Login", requiresAuth: false },
  },

  {
    path: "/:locale/login_passwordless_check",
    name: "login_passwordless_check",
    component: function () {
      return import("@/views/security/LoginPasswordlessCheckView.vue");
    },
    params: true,
    meta: { title: "Login Check for Passwordless", requiresAuth: false },
  },

  {
    path: "/:locale/login_passwordless",
    name: "login_passwordless",
    component: () => import("@/views/security/LoginPasswordlessView.vue"),
    meta: { title: "Login Passwordless", requiresAuth: false },
  },

  {
    path: "/:locale/self-registration/:token",
    name: "self_registration",
    component: () => import("@/views/security/SelfRegistrationView.vue"),
    meta: { title: "Registration", requiresAuth: false },
  },

  {
    path: "/projects",
    name: "projects",
    component: function () {
      return import("@/views/projects/ProjectList.vue");
    },
    meta: {
      title: "Projects",
      requiresAuth: true,
      permission: "VIEW_PROJECTS",
    },
  },

  {
    path: "/project/:productGroupId/create",
    name: "project_create",
    component: function () {
      return import("@/views/projects/ProjectCreate.vue");
    },
    meta: {
      title: "Project Create",
      requiresAuth: true,
      permission: "CREATE_PROJECT",
    },
  },

  {
    path: "/project/:id",
    name: "project_details",
    component: function () {
      return import("@/views/projects/ProjectDetails.vue");
    },
    meta: {
      title: "Project Details",
      requiresAuth: true,
      permission: "VIEW_PROJECTS",
    },
  },

  {
    path: "/project/:id/join",
    name: "project_join",
    component: function () {
      return import("@/views/projects/JoinProject.vue");
    },
    meta: {
      title: "Join Project",
      requiresAuth: true,
      permission: "VIEW_PROJECTS",
    },
  },

  {
    path: "/project/:id/assessment/create",
    name: "assessment_create",
    component: function () {
      return import("@/views/projects/participants/AssessmentCreate.vue");
    },
    meta: {
      title: "Assessment Create",
      requiresAuth: true,
      permission: "CREATE_ASSESSMENT",
    },
  },

  {
    path: "/project/assessment/:id",
    name: "assessmentDetails",
    props: true,
    component: function () {
      return import("@/views/projects/participants/AssessmentDetails.vue");
    },
    meta: {
      title: "Assessment Details",
      requiresAuth: true,
      permission: "VIEW_ASSESSMENTS",
    },
  },

  {
    path: "/project/copy/:id",
    name: "copyAssessments",
    props: true,
    component: function () {
      return import("@/views/projects/participants/CopyAssessments.vue");
    },
    meta: {
      title: "Copy Assessments",
      requiresAuth: true,
      permission: "COPY_ASSESSMENTS",
    },
  },

  {
    path: "/project/copy360/:id",
    name: "copyAssessments360",
    props: true,
    component: function () {
      return import("@/views/projects/participants/CopyAssessments360.vue");
    },
    meta: {
      title: "Copy 360 Assessments",
      requiresAuth: true,
      permission: "COPY_ASSESSMENTS",
    },
  },

  {
    path: "/project/:id/projectFeature/:projectFeatureId",
    name: "projectFeature",
    props: true,
    component: function () {
      return import("@/views/projects/features/Exercises.vue");
    },
    meta: {
      title: "Exercises",
      requiresAuth: true,
      permission: "MANAGE_PROJECT_FEATURES",
    },
  },
  {
    path: "/project/:id/projectOverview",
    name: "projectOverview",
    props: true,
    component: function () {
      return import("@/views/projects/ProjectOverview.vue");
    },
    meta: {
      title: "Project Overview",
      requiresAuth: true,
      permission: "VIEW_PROJECT_OVERVIEW",
    },
  },
  {
    path: "/reports",
    name: "reports",
    component: function () {
      return import("@/views/reports/ReportsList.vue");
    },
    meta: {
      title: "Reports",
      requiresAuth: true,
      permission: "VIEW_REPORTS",
    },
  },

  {
    path: "/profile/:id",
    name: "profile",
    component: function () {
      return import("@/views/ProfileView.vue");
    },
    meta: {
      title: "Profile",
      requiresAuth: true,
      permission: "VIEW_OWN_PROFILE",
    },
    beforeEnter: (to, from, next) => {
      const id = parseInt(to.params.id);
      if (isNaN(id)) {
        next({ name: "not-found" }); // redirect to not found page
      } else {
        next();
      }
    },
  },

  {
    path: "/project/:id/emails",
    name: "project_emails",
    component: function () {
      return import("@/views/projects/ProjectEmails.vue");
    },
    meta: {
      title: "Project Emails",
      requiresAuth: true,
      permission: "EDIT_EMAIL",
    },
  },
  {
    path: "/project/:id/editEmail",
    name: "edit_email",
    component: function () {
      return import("@/views/projects/EditEmail.vue");
    },
    meta: {
      title: "Edit Email",
      requiresAuth: true,
      permission: "EDIT_EMAIL",
    },
  },
  {
    path: "/companies",
    name: "companies",
    component: function () {
      return import("@/views/companies/CompanyList.vue");
    },
    meta: {
      title: "Companies",
      requiresAuth: true,
      permission: "VIEW_COMPANIES",
    },
  },
  {
    path: "/company/:id",
    name: "companyEdit",
    component: function () {
      return import("@/views/companies/CompanyEdit.vue");
    },
    meta: {
      title: "Company Edit",
      requiresAuth: true,
      permission: "EDIT_COMPANY",
    },
  },
  {
    path: "/company/create",
    name: "companyCreate",
    component: function () {
      return import("@/views/companies/CompanyCreate.vue");
    },
    meta: {
      title: "Company Create",
      requiresAuth: true,
      permission: "CREATE_COMPANY",
    },
  },

  {
    path: "/data-protection-notice",
    name: "dataProtectionNotice",
    component: function () {
      return import("@/views/DataProtectionNotice.vue");
    },
    meta: {
      title: "Data Protection Notice",
      requiresAuth: false,
    },
  },

  {
    path: "/agree2terms",
    name: "agree2terms",
    component: function () {
      return import("@/views/participant/Agree2Terms/Agree2Terms.vue");
    },
    meta: {
      title: "Agree2Terms",
      requiresAuth: true,
      permission: "AGREE_TO_TERMS",
    },
  },

  {
    path: "/survey/:id",
    name: "survey",
    component: function () {
      return import("@/views/participant/Questionnaire/Survey.vue");
    },
    meta: {
      title: "Survey",
      requiresAuth: true,
      permission: "TAKE_SURVEY",
    },
  },

  {
    path: "/survey/successLevels/:id",
    name: "successLevels",
    component: function () {
      return import("@/views/participant/Questionnaire/SuccessLevels.vue");
    },
    meta: {
      title: "Success Levels",
      requiresAuth: true,
      permission: "TAKE_SURVEY",
    },
  },

  {
    path: "/survey/classificationOfLevels/:id",
    name: "classificationOfLevels",
    component: function () {
      return import("@/views/participant/Questionnaire/ClassificationOfLevels.vue");
    },
    meta: {
      title: "Classification of Levels",
      requiresAuth: true,
      permission: "TAKE_SURVEY",
    },
  },

  {
    path: "/survey/evaluationLevel/:id",
    name: "evaluationLevel",
    component: function () {
      return import("@/views/participant/Questionnaire/EvaluationLevel.vue");
    },
    meta: {
      title: "Evaluation Level",
      requiresAuth: true,
      permission: "TAKE_SURVEY",
    },
  },

  {
    path: "/taskLanguages/:assessmentId/:id",
    name: "taskLanguages",
    component: function () {
      return import("@/components/Participant/Survey/TaskLanguages.vue");
    },
    meta: {
      title: "Task Languages",
      requiresAuth: true,
      permission: "TAKE_SURVEY",
    },
  },

  {
    path: "/excel/:id",
    name: "excel",
    component: function () {
      return import("@/views/projects/participants/ExcelUploader.vue");
    },
    meta: {
      title: "Excel",
      requiresAuth: true,
      permission: "UPLOAD_EXCEL",
    },
  },
  {
    path: "/keyUsers",
    name: "keyUsers",
    component: function () {
      return import("../views/users/KeyUserList.vue");
    },
    meta: {
      title: "Key users",
      requiresAuth: true,
      permission: "VIEW_KEY_USERS",
    },
  },

  {
    path: "/keyUser/:id",
    name: "keyUserEdit",
    component: function () {
      return import("@/views/users/KeyUserEdit.vue");
    },
    meta: {
      title: "Edit key user",
      requiresAuth: true,
      permission: "EDIT_KEY_USER",
    },
  },

  {
    path: "/keyUser/create",
    name: "keyUserCreate",
    component: function () {
      return import("@/views/users/KeyUserCreate.vue");
    },
    meta: {
      title: "Create key user",
      requiresAuth: true,
      permission: "CREATE_KEY_USER",
    },
  },

  {
    path: "/user/:id",
    name: "userEdit",
    component: function () {
      return import("@/views/users/UserEdit.vue");
    },
    meta: {
      title: "Edit user",
      requiresAuth: true,
      permission: "EDIT_PARTICIPANT",
    },
  },

  {
    path: "/users",
    name: "users",
    component: function () {
      return import("../views/users/UserList.vue");
    },
    meta: {
      title: "Users",
      requiresAuth: true,
      permission: "VIEW_PARTICIPANTS",
    },
  },

  {
    path: "/user/:id/info",
    name: "userInfo",
    component: function () {
      return import("@/views/users/UserInfo.vue");
    },
    meta: {
      title: "User Info",
      requiresAuth: true,
      permission: "VIEW_PARTICIPANT_INFO",
    },
  },

  {
    path: "/downloads",
    name: "downloads",
    component: function () {
      return import("@/views/downloads/Downloads.vue");
    },
    meta: {
      title: "Downloads",
      requiresAuth: true,
      permission: "VIEW_DOWNLOADS",
    },
  },

  {
    // Bez meta.permission: standard user ovde vidi samo "Shared with me".
    path: "/mindcloud/:folderId?",
    name: "mindcloud",
    component: function () {
      return import("@/views/mindcloud/MindCloudView.vue");
    },
    meta: {
      title: "MindCloud",
      requiresAuth: true,
    },
  },

  {
    path: "/profiles",
    name: "profiles",
    component: function () {
      return import("@/views/profiles/ProfilesList.vue");
    },
    meta: {
      title: "Profiles",
      requiresAuth: true,
      permission: "VIEW_PROFILES",
    },
  },

  {
    path: "/profile/:id/edit",
    name: "profile_edit",
    component: function () {
      return import("@/views/profiles/ProfileEdit.vue");
    },
    meta: {
      title: "Profile edit",
      requiresAuth: true,
      permission: "EDIT_PROFILE",
    },
  },

  {
    path: "/questionnaires",
    name: "questionnaires",
    component: function () {
      return import("@/views/questionnaires/QuestionnairesList.vue");
    },
    meta: {
      title: "Questionnaires",
      requiresAuth: true,
      permission: "VIEW_QUESTIONNAIRES",
    },
  },

  {
    path: "/questionnaire-companies",
    name: "questionnaireCompanyManagement",
    component: function () {
      return import("@/views/questionnaires/QuestionnaireCompanyManagement.vue");
    },
    meta: {
      title: "Questionnaire Company Management",
      requiresAuth: true,
      permission: "MANAGE_QUESTIONNAIRE_COMPANIES",
    },
  },

  {
    path: "/admin/logs",
    name: "adminLogs",
    component: function () {
      return import("@/views/admin/AdminLogsView.vue");
    },
    meta: {
      title: "Logs",
      requiresAuth: true,
      permission: "VIEW_AUDIT_LOGS",
    },
  },

  {
    path: "/questionnaire/:id",
    name: "questionnaireDetails",
    component: function () {
      return import("@/views/questionnaires/QuestionnaireDetails.vue");
    },
    meta: {
      title: "Questionnaire details",
      requiresAuth: true,
      permission: "VIEW_QUESTIONNAIRES",
    },
  },

  {
    path: "/questionnairePreview/:id",
    name: "questionnairePreview",
    component: function () {
      return import("@/views/questionnaires/QuestionnairePreview.vue");
    },
    meta: {
      title: "Questionnaire Preview",
      requiresAuth: true,
      permission: "PREVIEW_QUESTIONNAIRE",
    },
  },

  {
    path: "/writeReport/:taskId",
    name: "writeReport",
    component: function () {
      return import("@/views/participant/Questionnaire/WriteReport.vue");
    },
    meta: {
      title: "Write Custom Assessment Report",
      requiresAuth: true,
      permission: "WRITE_REPORT",
    },
  },

  /* Route for translation */

  {
    path: "/translation/overview",
    name: "translationOverview",
    component: function () {
      return import("@/views/translation/TranslationOverview.vue");
    },
    meta: {
      title: "Translation Overview",
      requiresAuth: true,
      permission: "VIEW_TRANSLATION_OVERVIEW",
    },
  },

  {
    path: "/translation/delegations",
    name: "translationDelegations",
    component: function () {
      return import("@/views/translation/TranslationDelegations.vue");
    },
    meta: {
      title: "Translation Delegations",
      requiresAuth: true,
      permission: "MANAGE_TRANSLATION_DELEGATIONS",
    },
  },

  {
    path: "/translation/groups",
    name: "translationGroups",
    component: function () {
      return import("@/views/translation/TranslationGroups.vue");
    },
    meta: {
      title: "Translation Groups",
      requiresAuth: true,
      permission: "MANAGE_TRANSLATION_GROUPS",
    },
  },

  {
    path: "/translation/list",
    name: "translationList",
    component: function () {
      return import("@/views/translation/TranslationList.vue");
    },
    meta: {
      title: "Translations",
      requiresAuth: true,
      permission: "MANAGE_TRANSLATIONS",
    },
  },

  {
    path: "/translation/:id/edit",
    name: "translationEdit",
    component: function () {
      return import("@/views/translation/TranslationEdit.vue");
    },
    meta: {
      title: "Edit Translation",
      requiresAuth: true,
      permission: "MANAGE_TRANSLATIONS",
    },
  },

  {
    path: "/questionnaire/:id/translation",
    name: "questionnaireTranslation",
    component: function () {
      return import("@/views/translation/QuestionnaireTranslation.vue");
    },
    meta: {
      title: "Questionnaire translation",
      requiresAuth: true,
      permission: "TRANSLATE_QUESTIONNAIRE",
    },
  },

  {
    path: "/project/assessment/:assessmentId/feedbackProviders",
    name: "feedbackProviders",
    component: function () {
      return import("@/views/projects/participants/FeedbackProviders.vue");
    },
    meta: {
      title: "Feedback Providers",
      requiresAuth: true,
      permission: "VIEW_FEEDBACK_PROVIDERS",
    },
  },

  {
    path: "/project/assessment/:assessmentId/feedbackProviders/:roleId",
    name: "inviteFeedbackProviders",
    component: function () {
      return import("@/views/projects/participants/InviteFeedbackProviders.vue");
    },
    meta: {
      title: "Invite Feedback Providers",
      requiresAuth: true,
      permission: "INVITE_FEEDBACK_PROVIDERS",
    },
  },

  {
    path: "/project/:id/uploadTargetGroups",
    name: "uploadTargetGroups",
    component: function () {
      return import("@/views/projects/participants/UploadTargetGroups.vue");
    },
    meta: {
      title: "Upload Target Groups",
      requiresAuth: true,
      permission: "UPLOAD_TARGET_GROUPS",
    },
  },

  {
    path: "/project/:id/targetGroupsSettings/:projectFeatureId",
    name: "targetGroupsSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/Comp360TargetGroup.vue");
    },
    meta: {
      title: "Target Groups Settings",
      requiresAuth: true,
      permission: "CONFIGURE_TARGET_GROUP_SETTINGS",
    },
  },

  {
    path: "/project/:id/surveySettings/:projectFeatureId",
    name: "surveySettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/Comp360SurveySettings.vue");
    },
    meta: {
      title: "Survey Settings",
      requiresAuth: true,
      permission: "CONFIGURE_SURVEY_SETTINGS",
    },
  },

  {
    path: "/project/:id/employeeSurveySettings/:projectFeatureId",
    name: "employeeSurveySettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/EmployeeSurveySettings.vue");
    },
    meta: {
      title: "Employee Survey Settings",
      requiresAuth: true,
      permission: "CONFIGURE_SURVEY_SETTINGS",
    },
  },

  {
    path: "/project/:id/criticalSuccessFactors/:projectFeatureId",
    name: "criticalSuccessFactors",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/CriticalSuccessFactors.vue");
    },
    meta: {
      title: "Critical Success Factors",
      requiresAuth: true,
      permission: "CONFIGURE_SUCCESS_FACTORS",
    },
  },

  {
    path: "/project/:id/evaluationLevel/:projectFeatureId",
    name: "evaluationLevelSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/EvaluationLevel.vue");
    },
    meta: {
      title: "Evaluation Level",
      requiresAuth: true,
      permission: "CONFIGURE_EVALUATION_LEVEL",
    },
  },

  {
    path: "/project/:id/previousAnswers/:projectFeatureId",
    name: "previousAnswersSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/PreviousAnswers.vue");
    },
    meta: {
      title: "Display Previous Answers",
      requiresAuth: true,
      permission: "CONFIGURE_PREVIOUS_ANSWERS",
    },
  },

  {
    path: "/project/:id/classificationLevels/:projectFeatureId",
    name: "classificationLevelsSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/ClassificationLevels.vue");
    },
    meta: {
      title: "Classification Levels",
      requiresAuth: true,
      permission: "CONFIGURE_CLASSIFICATION_LEVELS",
    },
  },

  {
    path: "/project/:id/limitationOfScores/:projectFeatureId",
    name: "limitationOfScoresSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/LimitationOfScores.vue");
    },
    meta: {
      title: "Limitation of Top & Low Scores",
      requiresAuth: true,
      permission: "CONFIGURE_LIMITATION_OF_SCORES",
    },
  },

  {
    path: "/project/:id/selfRegistration/:projectFeatureId",
    name: "selfRegistrationSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/SelfRegistration.vue");
    },
    meta: {
      title: "Self-registration",
      requiresAuth: true,
      permission: "CONFIGURE_SELF_REGISTRATION",
    },
  },

  {
    path: "/project/:id/competencyModelInQuestionnaire/:projectFeatureId",
    name: "competencyModelInQuestionnaireSettings",
    component: function () {
      return import("@/components/Modals/ProjectFeatures/CompetencyModelInQuestionnaire.vue");
    },
    meta: {
      title: "Competency Model in Questionnaire",
      requiresAuth: true,
      permission: "CONFIGURE_COMPETENCY_MODEL_IN_QUESTIONNAIRE",
    },
  },

  {
    path: "/project/:id/reportSettings/:projectFeatureId",
    name: "reportSettings",
    component: function () {
      return import("@/views/projects/ReportSettings.vue");
    },
    meta: {
      title: "Report Settings",
      requiresAuth: true,
      permission: "MANAGE_PROJECT_FEATURES",
    },
  },

  {
    path: "/target-group/:id",
    name: "targetGroup",
    component: function () {
      return import("@/views/participant/TargetGroup/TargetGroup.vue");
    },
    meta: {
      title: "Target Group",
      requiresAuth: true,
      permission: "VIEW_TARGET_GROUP",
    },
  },

  {
    path: "/logout",
    name: "logout",
    component: async (to, from, next) => {
      const auth = useAuthStore();
      await auth.logout();
    },
    meta: { title: "Logout", requiresAuth: true },
  },

  { path: "/:pathMatch(.*)*", component: NotFoundView, name: "not-found" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore();

  // SECURITY: Initialize auth on first navigation (auto-login with httpOnly cookie)
  // This gets a new access token using the refresh token stored in httpOnly cookie
  if (!auth.isInitialized && auth.hasSession) {
    try {
      await auth.initializeAuth();
    } catch (error) {
      console.error("Auth initialization failed:", error);
    }
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const title = to.meta.title;

  if (title) {
    document.title = `Mind One | ${title}`;
  } else {
    document.title = "Mind One";
  }

  // Update meta description dynamically (optional per route)
  const description =
    to.meta.description ||
    "Professional assessment and feedback management platform for organizations.";
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  }

  // Load translations for the current route language
  if (to.params.locale) {
    // Login pages: language comes from the invitation/login link
    localStorage.setItem("selectedLanguage", to.params.locale);
    await fetchTranslations(to.params.locale);
  } else if (to.name === "agree2terms") {
    // Show terms in the invitation language until the user confirms it
    const lang =
      localStorage.getItem("selectedLanguage") ||
      auth.user?.platformLanguageIsocode ||
      "en";
    await fetchTranslations(lang);
  } else {
    // Everywhere else: respect the user's current platform language setting
    const lang =
      auth.user?.platformLanguageIsocode ||
      localStorage.getItem("selectedLanguage") ||
      "en";
    await fetchTranslations(lang);
  }

  if (!requiresAuth) {
    if (
      auth.user &&
      (to.name === "login" || to.name === "login_passwordless")
    ) {
      return next({ name: "dashboard" });
    }
    return next();
  }

  if (to.path === "/logout") {
    await auth.logout();
    return;
  }

  // Check if user exists and has roles
  if (!auth.user || !auth.user.roles) {
    auth.returnUrl = to.fullPath;
    return next({ name: "login_passwordless", params: { locale: "en" } });
  }

  // Check token expiration but don't refresh here - let axios interceptor handle it
  const accessTokenExpiresAt = auth.accessTokenExpiresAt;
  const isAccessTokenExpired = Date.now() >= accessTokenExpiresAt;

  if (isAccessTokenExpired && !auth.hasSession) {
    // If token is expired and no session available, redirect to login
    auth.returnUrl = to.fullPath;
    return next({ name: "login_passwordless", params: { locale: "en" } });
  }

  // Check terms and conditions - required for ALL users, except clients
  // where the domain-specific env build sets VUE_APP_SKIP_TERMS_AGREEMENT=true
  if (
    !auth.user.isAcceptedTermsAndConditions &&
    to.path !== "/agree2terms" &&
    process.env.VUE_APP_SKIP_TERMS_AGREEMENT !== "true"
  ) {
    return next("/agree2terms");
  }

  // Check permissions (new system)
  const requiresPermission = to.matched.some(
    (record) => record.meta.permission,
  );
  if (requiresPermission) {
    const permission = to.meta.permission;
    const companyStore = (
      await import("@/stores/companyStore")
    ).useCompanyStore();
    const { hasPermission } = usePermissions();

    // Check if keyUserTypes are loaded
    const keyUserTypes = companyStore.keyUserTypes || [];

    if (!hasPermission(permission)) {
      // If keyUserTypes is empty (not yet loaded from CurrentCompany after login),
      // use auth.user.roles as temporary fallback
      // This handles the race condition where routing happens before CurrentCompany mounts
      if (keyUserTypes.length === 0) {
        const permissionRoles = PERMISSIONS[permission] || [];
        const hasRoleInAuth = permissionRoles.some(
          (role) => auth.user.roles && auth.user.roles.includes(role),
        );

        if (hasRoleInAuth) {
          // Allow access temporarily - proper keyUserTypes will be loaded by CurrentCompany
          // and subsequent navigations will use the correct company-scoped permissions
          console.info(
            `Allowing ${permission} via auth.user.roles fallback (keyUserTypes not yet loaded)`,
          );
        } else {
          console.warn(`Access denied: Missing permission '${permission}'`);
          return next("/");
        }
      } else {
        // keyUserTypes are loaded, permission check failed properly
        console.warn(`Access denied: Missing permission '${permission}'`);
        return next("/");
      }
    }
  }

  return next();
});

export default router;
